/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {Unit} from "./units.js";
import type {AuditBy, DB_Object, PartialProperties, TypedMap} from "@intuitionrobotics/ts-common";
import {ContactType, Gender} from "./contacts.js";
import type {ApiWithBody, ApiWithQuery} from "@intuitionrobotics/thunderstorm";
import type {DB_Contact_View, FriendRequestStatus} from "./db-contact-view.js";
import type {BucketData} from "./message.js";
import type {AuthType, Body_KasperoActivation} from "./activation.js";
import type {AnyDb_Contact} from "../shared/types.js";
// `import type` only — this file is consumed by the frontend; a value-mode
// import would drag firebase-admin into the Vite graph.
import type {BaseMessage} from "firebase-admin/messaging";
import type { InnerNotification, NotificationStatuses } from "./notification.js";

export type Landline_Data = {
    phoneNumber: string
    areaCode?: string
    fromVersion?: string
    toVersion?: string
    showPhoneNumber: boolean
    description: string
}
export type AddressDetails = {
    country: string
    city: string
    region?: string
    state?: string
    streetAddress?: string
    extendedAddress?: string
    postalCode?: string
    // Do I want this here?
    timezone?: string
};

export type FamilyMember = {
    // +1xxxxxxx is a string
    phoneNumber: string
    areaCode?: string
    email?: string
    requestedEmergencyMillis?: number
};

export type CareCenterUser = {};
export type GroupData = {
    type: string
};

export type UnitDataContact = Unit & {
    recognitionId?: string | number
    zendeskTicketId?: string
    phoneNumber?: string
    areaCode?: string
    email?: string
    organizationId?: string
    physicianId?: string
    careManagerId?: string
    authType?: AuthType
    language?: string
    userGroup?: string
};

export type AppInfo = {
    appId: string
};

// we need to change the way we put phoneNumber in data - cause familyMember is one of many reasons to contact with phone number -
// but still we need uniqueness for each type of phone number - for example putting in data the twilio static phoneNumber or something else for recognition.
export type ContactCommunicationMethod = {
    agentUser: UnitDataContact
    careCenterUser: CareCenterUser
    group: GroupData
    familyMember: FamilyMember
    application: AppInfo
    landline: Landline_Data
}

export type QueryContacts = Unit & {
    contactType?: keyof ContactCommunicationMethod
    userGroup?: string
    version?: string
    /**
     * Also return contacts whose connection to the unit owner was soft-deleted.
     * Off by default - a soft-deleted connection means the contact was removed and
     * must not surface on ElliQ or the phone app. Support/admin readers set it to
     * render a "deleted contacts" view. Deleted contacts carry `deleted: true` on
     * their connection to the owner.
     */
    includeDeleted?: boolean
};

export type ServerApiQueryContacts = {
    unitId: string,
    contactType?: keyof ContactCommunicationMethod
    contactId?: string
    contactServiceType?: ContactServiceType,
    phoneNumber?: string
    includeDeleted?: boolean
};

export type Body_ListByIds = {
    ids: string[]
};

export type QueryContactsByPhoneNumber = {
    phoneNumber: string
    contactType?: keyof ContactCommunicationMethod
};

export type ContactListResult = {
    contacts: DB_Contact_View<any>[];
};

export type DeleteContact_Body = DB_Object & { unit: Unit };
export type DeleteAgentUserContact_Body = {unitId: string, otherUnitId: string};
export type RequestAgentUserContact_Body = {unitId: string, otherUnitId: string};
export type Server_Api_DeleteContact_Body = DB_Object & { blame: string };
export type AddContactBody = {
    unit?: Unit,
    firstName: string
    lastName?: string
    relationship?: string
    phoneNumber: string
    emergencyContact?: boolean
    lastActivityApproved?: boolean
}
export type UpdateContactBody = {
    contactId: string,
    emergencyContact?: boolean
    firstName?: string
    lastName?: string
    SSML_Name?: string
    address?: AddressDetails
    phoneNumber?: string
    relationship?: ContactType
    lastActivityApproved?: boolean
    birthday?: number
    suggestedBirthday?: number
}

export type UpsertBody_ConnectionsData = {
    lastActivityApproved?: boolean
    role?: ContactType
    careGiver?: "true"|"false"|"pending"
    emergency?: boolean
    deleted?: boolean
    agentUserData?: {
        byUnit: Unit,
        metadata: { firstName?: string } & ConnectionMetadataOptionals
    }
}

export type Body_Server_Api_UpdateNameOfConnectedContact = {
    contactId: string
    connectionId: string
    name: string
}

export type Body_Server_Api_UpdateProfilePicture = {
    contactId: string
    connectionId?: string
    base64: string
    mimeType: string
}

export type Body_PatchContactWithProfilePicture = {
    contactView: DB_Contact_View<any>// The contact view that we have on the app
    connectionId?: string
    base64?: string
    mimeType?: string
    name?: string
}

export type Body_DeleteProfilePicture = {
    contactView: DB_Contact_View<any>// The contact view that we have on the app
    connectionId?: string
}

export type Body_Server_Api_DeleteProfilePicture = {
    contactId: string // The contact id that we have on the app
    connectionId?: string
}

type Body_CreateDefaultActivationContact = { unitId: string, blame: string };

export type Body_CreateActivationContact = (Body_KasperoActivation | Body_CreateDefaultActivationContact) & {active: boolean}

export type Body_DeleteUserOnlyTempFields = {unitId: string}
export type Body_UpsertUserOnlyTempFields = {
    unitId: string
    authType: AuthType
    organizationId: string
    physicianId?: string
    careManagerId?: string
}

export type CreateContact_Body = CreateContactGeneric_Body<'familyMember'>

export type CreateContactGeneric_Body<K extends keyof ContactCommunicationMethod> = {
    contact: PartialProperties<DB_Contact<K>, "_id">,
    connectionsData: UpsertBody_ConnectionsData
}

export type UpdatingContactGeneric_Body<K extends keyof ContactCommunicationMethod> = {
    contact: DB_Contact<K>,
    connectionsData: UpsertBody_ConnectionsData
}

export type PatchContact_Body = {
    contact: DB_Object & Partial<DB_Contact<any>>
}

export type Response_AddProfilePicture = {
    cacheId?: string,
    bucketData?: BucketData
}

export type Response_GetAppContacts = {
    agentUsers?: DB_Contact<any>[],
    appContact?: DB_Contact<"familyMember">,
    createdTime?: number
}

export type Response_DeleteProfilePicture = {
    contact: DB_Contact<any>
}

export type PushToPhone_Body = {
    phoneNumber: string, // Phone number of the contact
    pushKey: string, // Push notification key for the device
    status?: NotificationStatuses; // Status of the notification
    data: BaseMessage, // Additional data related to the notification
    notificationLocales?: { [key: string]: InnerNotification }, // Optional map of language initials to localized notification content
    insertNotificationIntoDbWithSilentPush?: boolean // Optional flag to indicate whether to insert the notification into the database with a silent push
}

export type Server_Api_QueryContacts_Response = {
    contacts: AnyDb_Contact[]
}

export type Server_Api_QueryContacts_ByUnit_Params = {
    contactType?: string
    firestoreKey?: string
    phoneNumber?: string
    contactServiceType?: ContactServiceType
    includeDeleted?: boolean
}

export type Body_SetEmergencyContact = {
    unitId: string,
    contactId: string,
    value: boolean
}

type groupDetails = {
    firstName: string
    lastName: string
    middleName: string
    groupType: string
}

type groupRenameDetails = DB_Object & {
    firstName: string
    lastName: string
    middleName: string
}

export type Body_CreateOrganizationGroups = {
    metadata: groupDetails[],
    blame: string
}

export type Body_RenameOrganizationGroups = {
    metadata: groupRenameDetails[],
    blame: string
}

export type Response_CreateOrganizationGroups = {
    ids: string[]
}

export type Response_UnlinkAgentUserFromAllGroups = {
    groupIds: string[]
}

export type Body_UnlinkGroups = {
    groupIds: string[]
}

export type Body_UnlinkAgentUserFromAllGroups = {
    contactId: string
}

export type Body_JoinExclusiveGroups = {
    contactId: string,
    groupIds: string[]
}

export type Body_AreContactsCareGiver = {
    contactIds: string[],
}

export type Response_AreContactsCareGiver = {
    areContactCareGiverMap: TypedMap<boolean>
}

export type Body_Server_Api_SetEmergencyContact = Body_SetEmergencyContact & { blame: string }

export type Server_Api_CreateOrganizationGroups = ApiWithBody<"/contacts/create-organization-groups", Body_CreateOrganizationGroups, Response_CreateOrganizationGroups>
export type Server_Api_RenameOrganizationGroups = ApiWithBody<"/contacts/rename-organization-groups", Body_RenameOrganizationGroups, void>
export type Server_Api_UnlinkGroups = ApiWithBody<"/contacts/unlink-groups", Body_UnlinkGroups, void>
export type Server_Api_UnlinkAgentUserFromAllGroups = ApiWithBody<"/contacts/unlink-agent-user-from-all-groups", Body_UnlinkAgentUserFromAllGroups, Response_UnlinkAgentUserFromAllGroups>
export type Server_Api_JoinExclusiveGroups = ApiWithBody<"/contacts/join-exclusive-groups", Body_JoinExclusiveGroups, void>

export type Api_SetEmergencyContact = ApiWithBody<"/v2/contacts/set-emergency-contact", Body_SetEmergencyContact, void>
export type Server_Api_SetEmergencyContact = ApiWithBody<"/contacts/set-emergency-contact", Body_Server_Api_SetEmergencyContact, void>

export type Api_PushToPhone = ApiWithBody<"/v2/contacts/push-to-phone", PushToPhone_Body, void>

export type Api_DeleteContact = ApiWithBody<"/v2/contacts/delete", DeleteContact_Body, void>
export type Api_DeleteAgentUserContact = ApiWithBody<"/v2/contacts/delete-agent-user", DeleteAgentUserContact_Body, void>
export type Api_RequestAgentUserContact = ApiWithBody<"/v2/contacts/request-agent-user", RequestAgentUserContact_Body, void>
export type Server_Api_DeleteContact = ApiWithBody<any, Server_Api_DeleteContact_Body, void>
export type Api_UpdateContact = ApiWithBody<"/v2/contacts/update", UpdateContactBody, DB_Contact<any>>
export type Api_AddContact_ByUnit = ApiWithBody<"/v2/contacts/add", AddContactBody, DB_Contact<'familyMember'>>

export type Api_CreateContact = ApiWithBody<"/v2/contacts/create", CreateContact_Body, DB_Contact<any>>
export type Server_Api_CreateContact = ApiWithBody<"/contacts/create", CreateContact_Body, DB_Contact<any>>
export type Api_PatchContact = ApiWithBody<"/v2/contacts/patch", PatchContact_Body, DB_Contact<any>>
export type Server_Api_PatchContact = ApiWithBody<"/contacts/update", PatchContact_Body, DB_Contact<any>>

export type Server_Api_QueryContact_ByUnit = ApiWithQuery<any, Server_Api_QueryContacts_Response, Server_Api_QueryContacts_ByUnit_Params>
export type Server_Api_QueryContact = ApiWithQuery<any, Server_Api_QueryContacts_Response>

export type Api_HardDeleteContact = ApiWithBody<"/v2/contacts/hard-delete", DeleteContact_Body, void>
export type Server_Api_HardDeleteContact = ApiWithBody<any, Server_Api_DeleteContact_Body, void>
export type Api_GetUnitContacts = ApiWithBody<"/v2/contacts/get-by-unit", QueryContacts, DB_Contact<any>[]>
export type Api_ListContactsByIds = ApiWithBody<"/v2/contacts/list-by-ids", Body_ListByIds, DB_Contact<any>[]>
export type Api_GetUnitContacts_View = ApiWithBody<"/v2/contacts/get-by-unit", QueryContacts, DB_Contact_View<any>[]>
export type Api_GetFamilyMemberContacts = ApiWithBody<"/v2/contacts/get-by-phone-number", QueryContactsByPhoneNumber, DB_Contact<any>[]>
export type Api_GetByAppSession = ApiWithBody<"/v2/contacts/app/get-by-session", {}, DB_Contact<any>[]>
export type Api_GetAppContacts = ApiWithBody<"/v2/contacts/app/get", {}, Response_GetAppContacts>
export type Api_GetBySession = ApiWithBody<"/v2/contacts/get-by-session", {}, DB_Contact<any>[]>
export type Api_ListUnitContacts = ApiWithBody<"/v2/contacts/list-by-unit", QueryContacts, ContactListResult>
export type Api_GetAgentUserContact = ApiWithBody<"/v2/contacts/get-agent-user", Unit, DB_Contact<"agentUser">>
export type Api_GetWithConnections = ApiWithQuery<"/v2/contacts/get-with-connections", DB_Contact<keyof ContactCommunicationMethod>[], DB_Object>;
export type Server_Api_GetWithConnections = ApiWithQuery<`/contacts/get-with-connections/${string}`, {contacts: DB_Contact<keyof ContactCommunicationMethod>[]}, DB_Object>;
export type Server_Api_AreContactsCaregivers = ApiWithBody<any, Body_AreContactsCareGiver, Response_AreContactsCareGiver>;

export type Server_Api_UpdateNameOfConnectedContact = ApiWithBody<"/contacts/update-name-of-connected-contact", Body_Server_Api_UpdateNameOfConnectedContact, void>
export type Server_Api_UpdateProfilePicture = ApiWithBody<"/contacts/update-profile-picture", Body_Server_Api_UpdateProfilePicture, Response_AddProfilePicture>

export type Api_AddContactProfilePhotoContact = ApiWithBody<"/v2/contacts/update-profile-picture", Body_PatchContactWithProfilePicture, Response_AddProfilePicture>
export type Api_DeleteProfilePicture = ApiWithBody<"/v2/contacts/delete-profile-picture", Body_DeleteProfilePicture, Response_DeleteProfilePicture>
export type Server_Api_DeleteProfilePicture = ApiWithBody<"/contacts/delete-profile-picture", Body_Server_Api_DeleteProfilePicture, Response_DeleteProfilePicture>
export type Server_Api_DeleteAgentUserContact = ApiWithBody<`/messaging/contacts/connection/delete/${string}/to/${string}`, {}, DB_Contact<"agentUser">>
export type Server_Api_RequestAgentUserContact = ApiWithBody<`/messaging/contacts/connection/request/${string}/to/${string}`, {}, DB_Contact<"agentUser">>

export type Server_Api_CreateActivationContact = ApiWithBody<"/contacts/create-activation-contact", Body_CreateActivationContact, DB_Contact<"agentUser">>

export type Server_Api_UpsertUserOnlyTempFields = ApiWithBody<"/contacts/upsert-user-pending-activation", Body_UpsertUserOnlyTempFields, void>
export type Server_Api_DeleteUserOnlyTempFields = ApiWithBody<"/contacts/delete-user-pending-activation", Body_DeleteUserOnlyTempFields, void>

export enum ContactServiceType {
    PCP = "PCP",
    TRANSPORT = "TRANSPORT"
}

export type Body_PcpContact = {
    firstName: string
    lastName?: string
    phoneNumber: string
    areaCode: string
    gender: Gender
    blame: string
}

export type Body_Api_PcpContact = Omit<Body_PcpContact, "blame"> & { agentId: string }

export type Api_GetPcpContact = ApiWithQuery<`/v2/contacts/get-pcp?agentId=${string}`, DB_Contact<any> | null, { agentId: string }>
export type Api_CreatePcpContact = ApiWithBody<"/v2/contacts/create-pcp", Body_Api_PcpContact, DB_Contact<any>>
export type Api_UpdatePcpContact = ApiWithBody<"/v2/contacts/update-pcp", Body_Api_PcpContact, DB_Contact<any>>

export type Communication<K extends keyof ContactCommunicationMethod> = {
    contactType: K
    contactData: ContactCommunicationMethod[K]
}

export enum Relation {
    AGENT_USER = "AGENT_USER",
    ELLIQ_CONTACT = "ELLIQ_CONTACT",
    DOCTOR = "DOCTOR",
    FAMILY_MEMBER = "FAMILY_MEMBER",
    CARE_MANAGER = "CARE_MANAGER",
    CASE_MANAGER = "CASE_MANAGER",
    CARE_CENTER = "CARE_CENTER",
    GROUP = "GROUP",
    GROUP_MEMBER = "GROUP_MEMBER",
    UNKNOWN = "UNKNOWN"
}

export type ConnectionMetadata = ConnectionMetadataOptionals & {
    firstName: string
}

export type ConnectionMetadataOptionals = {
    lastName?: string,
    SSML_Name?: string
}

export type ContactConnection = {
    _id: string
    relation: Relation
    lastActivityApproved?: boolean
    // Might be confusing but this is the old contactType, which is actually role: sister, in law, son, etc
    role?: ContactType
    emergency?: boolean
    deleted?: boolean
    metadata?: ConnectionMetadata
    bucketData?: BucketData | null
    careGiver?: "true" | "false" | "pending"
    friendRequestStatus?: FriendRequestStatus
}

export type DB_Contact<K extends keyof ContactCommunicationMethod> =
    DB_Object
    & Communication<K>
    & {
    firstName: string
    middleName?: string
    lastName?: string
    SSML_Name?: string
    birthday?: number
    birthdayDate?: string
    _birthday?: number
    profilePictureUrl?: string
    profilePictureData?: BucketData | null
    // This is currently needed for agentUser contact that is bounded to a unit
    active?: boolean
    address?: AddressDetails
    gender?: Gender
    contactServiceType?: ContactServiceType | null
    connections: ContactConnection[]
    __connectionIds: string[]
    deleted?: boolean // deprecated
    _created: AuditBy
    _updated: AuditBy
}

export enum ContactActions {
    HARD_DELETE = "hard_delete",
    SOFT_DELETE = "soft_delete",
    CREATE = "create",
    UPDATE = "update"
}

export type Contact_Fact = {
    creator: string,
    contactId: string,
    actionStatus: ContactActions,
    contactType: string,
    care_giver_status?: string,
    organizationName?: string,
    _auditBy: string,
    timeStamp: number,
    factType: string,
    emergencyContact: boolean,
    relation?: string
    phoneNumberExists: boolean
    rootExecutionId?: string
    originator?: string
    dateOfBirth?: string,
    gender?: string
}

export const getReflectingRelation = (name: string, gender?: Gender, connection?: ContactConnection) => {
    if (!connection)
        return "your loved one"

    switch (connection.role) {

        case ContactType.Daughter:
        case ContactType.Son:
        case ContactType.Child:
            return !gender ? "your parent" : (gender === Gender.Male ? "dad" : "mom")
        case ContactType.GrandDaughter:
        case ContactType.GrandSon:
        case ContactType.GrandChild:
            return !gender ? "your grandparent" : (gender === Gender.Male ? "grandpa" : "grandma")
        case ContactType.Spouse:
        case ContactType.ElderSpouse:
            return !gender ? "your spouse" : (gender === Gender.Male ? "your husband" : "your wife")
        case ContactType.Doctor:
            return "your patient"
        case ContactType.Cousin:
            return "your cousin"
        case ContactType.NephewNiece:
            return !gender ? "your relative" : (gender === Gender.Male ? "your uncle" : "your aunt")
        case ContactType.Friend:
            return "your friend"
        case ContactType.Sister:
        case ContactType.Brother:
        case ContactType.Sibling:
            return !gender ? "your sibling" : (gender === Gender.Male ? "your brother" : "your sister")
        case ContactType.FamilyMember:
            return "your relative"
        case ContactType.Neighbour:
            return "your neighbour"
        case ContactType.ProfessionalCareGiver:
        case ContactType.CareGiver:
        case ContactType.CaseManager:
        case ContactType.Other:
            return name;
        default:
            return "your loved one"
    }
};

export const getRelationToMention = (firstName: string, lastName: string, relation: ContactType, gender?: Gender) => {
    const isGenderSpecified = gender && gender !== Gender.Empty;

    let relationMention = "";
    switch (relation) {
        case ContactType.Doctor:
            return `your ${relation.toLowerCase()}, ${firstName} ${lastName},`;
        case ContactType.CareGiver:
        case ContactType.FamilyCareGiver:
        case ContactType.ProfessionalCareGiver:
            return `your caregiver, ${firstName} ${lastName},`;
        case ContactType.Child:
            relationMention = isGenderSpecified ?
                (gender === Gender.Female ? ContactType.Daughter.toLowerCase() : ContactType.Son.toLowerCase()) :
                relation.toLowerCase();
            break;
        case ContactType.GrandChild:
            relationMention = isGenderSpecified ?
                (gender === Gender.Female ? ContactType.GrandDaughter.toLowerCase() : ContactType.GrandSon.toLowerCase()) :
                relation.toLowerCase();
            break;
        case ContactType.ElderSpouse:
        case ContactType.Spouse:
            relationMention = isGenderSpecified ?
                (gender === Gender.Female ? "wife" : "husband") :
                ContactType.Spouse.toLowerCase();
            break;
        case ContactType.Sibling:
            relationMention = isGenderSpecified ?
                (gender === Gender.Female ? ContactType.Sister.toLowerCase() : ContactType.Brother.toLowerCase()) :
                relation.toLowerCase();
            break;
        case ContactType.NephewNiece:
            relationMention = isGenderSpecified ?
                (gender === Gender.Female ? "niece" : "nephew") : "";
            break;
        case ContactType.InLaw:
            relationMention = "in-law";
            break;
        case ContactType.Cousin:
        case ContactType.Friend:
        case ContactType.Daughter:
        case ContactType.GrandDaughter:
        case ContactType.Son:
        case ContactType.GrandSon:
        case ContactType.Sister:
        case ContactType.Brother:
        case ContactType.Neighbour:
            relationMention = relation.toLowerCase();
            break;
        default:
            break;
    }
    return relationMention !== "" ? `your ${relationMention} ${firstName}` : `${firstName} ${lastName}`;
}
